import type { Quote, QuoteCrewMember, ResidentialFlatRateRoom, ResidentialFlatRates, ResourceSlug } from './types';

/** A crew/truck resource the quote bills whose hourly rate is $0. */
export interface ZeroRateResource {
  slug: ResourceSlug;
  name: string;
}

/** Why a residential flat-rate quote cannot be priced from the org's bedroom tiers. */
export type FlatRateTierIssue =
  /** The org has no flat-rate configuration at all. */
  | 'no_flat_rates'
  /** No tier exists for the quote's bedroom count. */
  | 'unmatched'
  /** The matching tier is switched off. */
  | 'disabled'
  /** The matching tier is enabled but priced at $0. */
  | 'zero';

export interface FormulaReadiness {
  ready: boolean;
  missing: string[];
  /** True when the only missing requirement is the rate card — enables the
   *  "View & Adjust Estimate" link so users can fill in rates. */
  onlyMissingRateCard: boolean;
  /** True when the quote is priced by the org's residential flat-rate tiers
   *  (residential quote, org pricing model `flat_rate`, no per-quote override). */
  isFlatRate: boolean;
  /** Hourly-priced quotes: every resource the quote bills whose rate is $0.
   *  Non-empty exactly when "rate card" is in `missing` for an hourly quote. */
  zeroRateResources: ZeroRateResource[];
  /** Flat-rate quotes: why the bedroom tier can't price the move, when it can't. */
  flatRateTierIssue?: FlatRateTierIssue;
}

export interface FormulaReadinessOptions {
  /** Crew with the rates the engine would actually use: `quote.crew` when the
   *  quote has snapshotted its rates, otherwise the org's default rates
   *  (`buildCrewFromOrgRates(orgRates)`). Falls back to `quote.crew`. */
  effectiveCrew?: QuoteCrewMember[];
  /** The org's `useBlendedTruckDriverRate` flag. Decides whether the quote
   *  bills a single "Truck + Driver" line or separate Driver and Truck lines. */
  useBlendedTruckDriverRate?: boolean;
  /** The org's `residentialPricingModel`. Defaults to `hourly_rate`. */
  residentialPricingModel?: 'flat_rate' | 'hourly_rate';
  /** The org's residential flat-rate tiers. Only read for flat-rate quotes.
   *  `undefined` = not loaded yet (tier check skipped); `null` = the org has none. */
  flatRates?: ResidentialFlatRates | null;
}

const RESOURCE_LABEL: Record<ResourceSlug, string> = {
  supervisor: 'Supervisor',
  mover: 'Mover',
  driver: 'Driver',
  truck: 'Truck',
  truck_driver: 'Truck + Driver',
};

/** Human label for a crew/truck resource slug. */
export function resourceRateLabel(slug: ResourceSlug): string {
  return RESOURCE_LABEL[slug];
}

/**
 * True when a residential quote is priced from the org's flat-rate tiers
 * rather than hourly crew rates.
 */
export function isFlatRateQuote(
  quote: Pick<Quote, 'type' | 'pricingOverride'> | null | undefined,
  residentialPricingModel: 'flat_rate' | 'hourly_rate' | undefined,
): boolean {
  return !!quote
    && quote.type === 'residential'
    && residentialPricingModel === 'flat_rate'
    && !quote.pricingOverride;
}

/**
 * The crew/truck resources an hourly quote bills, i.e. the rates that must be
 * non-zero for the estimate to be meaningful.
 *
 * Commercial: Supervisor and Mover always, plus Truck + Driver when the org
 * uses the blended rate, otherwise Driver and Truck. Mirrors the commercial
 * cost lines in `computeFormula` (Σ quantity × rate).
 *
 * Residential (hourly): Supervisor and Mover. Residential crews bill trucks
 * separately but an org may legitimately fold the truck into the mover rate,
 * so a $0 truck rate is not treated as a missing rate card.
 */
export function getRequiredRateSlugs(
  quoteType: 'commercial' | 'residential' | undefined,
  useBlendedTruckDriverRate: boolean | undefined,
): ResourceSlug[] {
  if (quoteType === 'residential') return ['supervisor', 'mover'];
  return useBlendedTruckDriverRate
    ? ['supervisor', 'mover', 'truck_driver']
    : ['supervisor', 'mover', 'driver', 'truck'];
}

/**
 * Resources the quote bills whose hourly rate is missing or $0.
 * A resource absent from `crew` counts as $0 — that is exactly how the engine
 * prices it (`crewRateMap.get(slug) ?? 0`).
 */
export function findZeroRateResources(
  crew: QuoteCrewMember[] | null | undefined,
  quoteType: 'commercial' | 'residential' | undefined,
  useBlendedTruckDriverRate: boolean | undefined,
): ZeroRateResource[] {
  const crewBySlug = new Map((crew ?? []).map(c => [c.slug, c]));
  return getRequiredRateSlugs(quoteType, useBlendedTruckDriverRate)
    .filter(slug => (crewBySlug.get(slug)?.hourlyRate ?? 0) <= 0)
    .map(slug => ({ slug, name: crewBySlug.get(slug)?.name || RESOURCE_LABEL[slug] }));
}

/** Bedroom count a residential quote is priced on, or `undefined` when unset. */
export function getQuoteBedroomCount(
  quote: Pick<Quote, 'numberOfBedrooms' | 'addresses'> | null | undefined,
): number | undefined {
  const bedrooms = quote?.numberOfBedrooms ?? quote?.addresses?.origin?.residentialProperty?.bedrooms;
  return bedrooms == null ? undefined : bedrooms;
}

/**
 * Matches a bedroom count to the org's flat-rate tier the same way
 * `computeResidentialFlatRateFormula` does (counts of 5 or more share the
 * "5+" tier) and reports whether that tier can price a move.
 */
export function matchFlatRateTier(
  flatRates: ResidentialFlatRates | null | undefined,
  bedrooms: number,
): { tier: ResidentialFlatRateRoom | undefined; issue?: FlatRateTierIssue } {
  if (!flatRates) return { tier: undefined, issue: 'no_flat_rates' };
  const bedroomKey = Math.min(bedrooms, 5);
  const tier = flatRates.rooms.find(r => r.bedroomCount === bedroomKey);
  if (!tier) return { tier: undefined, issue: 'unmatched' };
  if (!tier.enabled) return { tier, issue: 'disabled' };
  if ((tier.flatRate ?? 0) <= 0) return { tier, issue: 'zero' };
  return { tier };
}

/**
 * Checks whether a quote meets all criteria required to run the formula engine.
 *
 * Commercial criteria:
 *   1. At least one inventory item (casegoods or modular workstations)
 *   2. Origin address set
 *   3. Destination address set (waived for internal moves)
 *   4. Move timing set (day / evening / weekend)
 *   5. Origin logistics has at least one strides value (Leg 1 or Leg 2)
 *   6. Destination logistics has at least one strides value (waived for internal moves)
 *   7. Rate card: every resource the quote bills has a non-zero hourly rate —
 *      Supervisor, Mover and either Truck + Driver (blended) or Driver + Truck
 *
 * Residential hourly criteria:
 *   1. At least one room with at least one inventory item
 *   2. Origin address set
 *   3. Destination address set (waived for internal moves)
 *   4. Move date set
 *   5. Rate card: supervisor and mover hourly rates must be non-zero
 *   (Strides are NOT required for residential.)
 *
 * Residential flat-rate criteria (org `residentialPricingModel === 'flat_rate'`
 * and no per-quote `pricingOverride`) — the flat-rate engine needs neither
 * inventory, strides nor timing:
 *   1. Number of bedrooms set (on the quote or the origin property)
 *   2. Rate card: the matching bedroom tier exists, is enabled and is priced
 *      above $0 (skipped while `options.flatRates` is still undefined/loading)
 *
 * @param quote   The quote to check.
 * @param options Effective crew rates and the org settings the rate-card
 *                checks depend on. A bare `QuoteCrewMember[]` is accepted for
 *                backward compatibility and treated as `effectiveCrew`.
 */
export function checkFormulaReadiness(
  quote: Quote | null | undefined,
  options: FormulaReadinessOptions | QuoteCrewMember[] = {},
): FormulaReadiness {
  const opts: FormulaReadinessOptions = Array.isArray(options) ? { effectiveCrew: options } : options;
  const flatRate = isFlatRateQuote(quote, opts.residentialPricingModel);

  if (!quote) {
    return { ready: false, missing: [], onlyMissingRateCard: false, isFlatRate: flatRate, zeroRateResources: [] };
  }

  const missing: string[] = [];
  const isResidential = quote.type === 'residential';
  const crewRates = opts.effectiveCrew ?? quote.crew ?? [];

  if (flatRate) {
    // Flat-rate residential: priced from the bedroom tier, so the only inputs
    // the engine needs are the bedroom count and a usable tier.
    let flatRateTierIssue: FlatRateTierIssue | undefined;
    const bedrooms = getQuoteBedroomCount(quote);
    if (bedrooms === undefined) {
      missing.push('number of bedrooms');
    } else if (opts.flatRates !== undefined) {
      const { issue } = matchFlatRateTier(opts.flatRates, bedrooms);
      if (issue) {
        flatRateTierIssue = issue;
        missing.push('rate card');
      }
    }

    const onlyMissingRateCard = missing.length === 1 && missing[0] === 'rate card';
    return {
      ready: missing.length === 0,
      missing,
      onlyMissingRateCard,
      isFlatRate: true,
      zeroRateResources: [],
      flatRateTierIssue,
    };
  }

  // 1. Inventory — residential uses rooms with items, commercial uses casegoods/modular
  if (isResidential) {
    const hasRoomWithItem = (quote.residentialRooms ?? []).some(
      r => r.items && r.items.length > 0,
    );
    if (!hasRoomWithItem) {
      missing.push('rooms with inventory');
    }
  } else {
    const hasCasegoods = (quote.casegoodsInventory?.length ?? 0) > 0;
    const hasModular = (quote.modularWorkstations?.totalWorkstations ?? 0) > 0;
    if (!hasCasegoods && !hasModular) {
      missing.push('inventory items');
    }
  }

  // 2. Origin address
  if (!quote.addresses?.origin?.address) {
    missing.push('origin address');
  }

  // 3. Destination address (skip for internal move)
  const isInternal = quote.addresses?.internalMove ?? false;
  if (!isInternal && !quote.addresses?.destination?.address) {
    missing.push('destination address');
  }

  if (isResidential) {
    // 4r. Move date (residential uses preferredDate, not timing)
    if (!quote.movingDate?.preferredDate) {
      missing.push('move date');
    }
  } else {
    // 4c. Move timing (commercial)
    if (!quote.movingDate?.timing) {
      missing.push('move timing');
    }

    // 5c. Origin strides (commercial only)
    const hasOriginStrides =
      (quote.originLogistics?.strides1 ?? 0) > 0 ||
      (quote.originLogistics?.strides2 ?? 0) > 0;
    if (!hasOriginStrides) {
      missing.push('origin strides');
    }

    // 6c. Destination strides (commercial only, skip for internal move)
    if (!isInternal) {
      const hasDestStrides =
        (quote.destinationLogistics?.strides1 ?? 0) > 0 ||
        (quote.destinationLogistics?.strides2 ?? 0) > 0;
      if (!hasDestStrides) {
        missing.push('destination strides');
      }
    }
  }

  // 5r / 7c. Rate card — every resource the quote bills needs a non-zero rate.
  //          Uses effectiveCrew (which may include org-default rates) if provided.
  const zeroRateResources = findZeroRateResources(crewRates, quote.type, opts.useBlendedTruckDriverRate);
  if (zeroRateResources.length > 0) {
    missing.push('rate card');
  }

  const onlyMissingRateCard = missing.length === 1 && missing[0] === 'rate card';

  return { ready: missing.length === 0, missing, onlyMissingRateCard, isFlatRate: false, zeroRateResources };
}

/** Where an org admin fixes a missing rate card, in the words the app uses. */
export const RATE_CARD_ADMIN_PATH = 'Admin → Quoting → Rate Cards';

/**
 * One sentence explaining why the rate card counts as missing, or `null`
 * when it is not. Shared by web and mobile so both say the same thing.
 */
export function describeMissingRateCard(readiness: FormulaReadiness): string | null {
  if (!readiness.missing.includes('rate card')) return null;
  if (readiness.isFlatRate) {
    switch (readiness.flatRateTierIssue) {
      case 'no_flat_rates': return 'No residential flat rates are set up for your company.';
      case 'unmatched': return 'No residential flat-rate tier matches this bedroom count.';
      case 'disabled': return 'The residential flat-rate tier for this bedroom count is turned off.';
      default: return 'The residential flat-rate tier for this bedroom count is $0.';
    }
  }
  const names = readiness.zeroRateResources.map(r => r.name);
  if (names.length === 0) return 'One or more crew or truck rates are $0.';
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} ${names.length === 1 ? 'has' : 'have'} a $0 hourly rate.`;
}

/**
 * Produces a stable string fingerprint of all formula input fields.
 * Used to detect when inputs have changed and a recalculation is needed.
 *
 * Intentionally excludes `quote.formula` (the output) to prevent
 * infinite recalculation loops when the formula is saved back to the quote.
 */
/**
 * Addresses as they count toward the fingerprint. The transit block is
 * recomputed on every page load (same distance and duration, new
 * `calculatedAt`), so the timestamp is left out: otherwise a refresh would
 * re-run the engine and wipe the estimator's manual adjustments.
 */
function addressesForFingerprint(addresses: Quote['addresses']): unknown {
  if (!addresses?.transit) return addresses;
  const { calculatedAt: _ignored, ...transit } = addresses.transit as unknown as Record<string, unknown>;
  return { ...addresses, transit };
}

export function getFormulaFingerprint(quote: Quote | null | undefined): string {
  if (!quote) return '';
  return JSON.stringify({
    ol: quote.originLogistics,
    dl: quote.destinationLogistics,
    md: quote.movingDate,
    a: addressesForFingerprint(quote.addresses),
    tj: quote.travelToJob,
    ttr: quote.travelTimeRate,
    em: quote.efficiencyMultiplier,
    ci: quote.casegoodsInventory,
    rr: quote.residentialRooms,
    mw: quote.modularWorkstations,
    // Materials cost depends on the move's rental length.
    mrd: quote.materialsRentalDays,
    // crew excluded: rate/quantity changes are handled directly by saveRates on the
    // breakdown page and do not require a full formula re-run.
  });
}
